import { llmIdMapper } from './llmIdMapper';

// List of random words for ID generation
const RANDOM_WORDS = [
  'apple', 'banana', 'cherry', 'dragon', 'eagle', 'forest', 'garden', 'house',
  'island', 'jungle', 'knight', 'lemon', 'mountain', 'ocean', 'planet', 'queen',
  'river', 'star', 'tiger', 'umbrella', 'village', 'water', 'xylophone', 'yellow',
  'zebra', 'anchor', 'bridge', 'castle', 'diamond', 'elephant', 'flower', 'guitar',
  'hammer', 'iceberg', 'jacket', 'kangaroo', 'lighthouse', 'monkey', 'notebook',
  'orange', 'penguin', 'quilt', 'rainbow', 'sunset', 'turtle', 'violin', 'window',
  'xylophone', 'yacht', 'zebra', 'airplane', 'butterfly', 'camera', 'dolphin',
  'feather', 'giraffe', 'hamburger', 'igloo', 'jellyfish', 'keyboard', 'laptop',
  'mushroom', 'necklace', 'octopus', 'piano', 'quokka', 'robot', 'snowflake',
  'telephone', 'umbrella', 'volcano', 'whale', 'xylophone', 'yogurt', 'zucchini',
  'adventure', 'bicycle', 'compass', 'doorway', 'emerald', 'falcon', 'glacier',
  'horizon', 'infinity', 'journey', 'kaleidoscope', 'lantern', 'meadow', 'nebula',
  'oasis', 'prism', 'quantum', 'radiance', 'sapphire', 'thunder', 'universe',
  'vortex', 'whisper', 'xenon', 'yearning', 'zenith', 'aurora', 'blizzard',
  'cascade', 'driftwood', 'eclipse', 'firefly', 'galaxy', 'harmony', 'illusion',
  'jasmine', 'kinetic', 'labyrinth', 'mirage', 'nimbus', 'orbit', 'phoenix',
  'quicksilver', 'resonance', 'serenity', 'tempest', 'utopia', 'velocity',
  'wanderer', 'xerophyte', 'yonder', 'zephyr', 'beacon', 'crystal', 'dynamo',
  'essence', 'fusion', 'gravity', 'helix', 'impulse', 'jovial', 'kinship',
  'luminous', 'matrix', 'nexus', 'onyx', 'paradox', 'quasar', 'rhythm',
  'spectrum', 'twilight', 'unity', 'vivid', 'wisdom', 'xenial', 'youth', 'zodiac'
];

/**
 * Generates a random entity ID in the format: entityType_three_random_words
 * Example: "doc_horse_monkey_house"
 */
export function generateEntityId(entityType: string): string {
  // Convert entity type to lowercase and clean it
  const cleanType = entityType.toLowerCase().replace(/[^a-z0-9]/g, '');
  
  // Select 3 random words
  const words = [];
  for (let i = 0; i < 3; i++) {
    const randomIndex = Math.floor(Math.random() * RANDOM_WORDS.length);
    words.push(RANDOM_WORDS[randomIndex]);
  }
  
  // Combine: entityType_word1_word2_word3
  return `${cleanType}_${words.join('_')}`;
}

// Abstract patterns for node IDs (no semantic meaning)
const SEGMENTS = ['abc', 'def', 'ghi', 'jkl', 'mno', 'pqr', 'stu', 'vwx', 'yz1', 'a2b', 'c3d', 'e4f'];
const NUMBERS = ['123', '456', '789', '012', '345', '678', '901', '234', '567', '890', '135', '246'];  
const SUFFIXES = ['xyz', 'pqr', 'def', 'ghi', 'jkl', 'mno', 'abc', 'stu', 'vwx', 'yz1', 'x2y', 'z3a'];

/**
 * Generates a 4-digit document prefix for node traceability
 * Example: "1234", "5678"
 */
export function generateDocumentPrefix(): string {
  return Math.floor(1000 + Math.random() * 9000).toString();
}

/**
 * Generates an abstract pattern node ID with document prefix
 * Example: "1234-abc-123-xyz" (doc 1234, abstract pattern)
 */
export function generateNodeId(documentPrefix?: string): string {
  // Generate abstract pattern
  const segment = SEGMENTS[Math.floor(Math.random() * SEGMENTS.length)];
  const number = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
  const suffix = SUFFIXES[Math.floor(Math.random() * SUFFIXES.length)];
  
  const abstractPattern = `${segment}-${number}-${suffix}`;
  
  // Return with or without document prefix
  return documentPrefix ? `${documentPrefix}-${abstractPattern}` : abstractPattern;
}

/**
 * Generates an abstract pattern without document prefix (for standalone use)
 * Example: "abc-123-xyz"
 */
export function generateAbstractNodeId(): string {
  return generateNodeId();
}

/**
 * Generates a unique LLMId that doesn't already exist in the database
 * Example: "doc_horse_monkey_house"
 */
export async function generateUniqueLLMId(entityType: string, checkExistence: (id: string) => Promise<boolean>): Promise<string> {
  const firstAttempt = generateEntityId(entityType);
  
  const existsInCache = llmIdMapper.getActualId(firstAttempt) !== null;
  if (!existsInCache) {
    return firstAttempt;
  }
  
  // Only if the ID exists in our local cache, do the full collision detection
  let attempts = 1; // We already tried once
  const maxAttempts = 5; // Reduced since collisions are rare
  
  while (attempts < maxAttempts) {
    const llmId = generateEntityId(entityType);
    
    // Check if this ID already exists
    const exists = await checkExistence(llmId);
    
    if (!exists) {
      return llmId;
    }
    
    attempts++;
  }
  
  // If we've tried too many times, fall back to a timestamp-based ID
  const timestamp = Date.now();
  const fallbackId = `${entityType.toLowerCase()}_${timestamp}_${Math.random().toString(36).substr(2, 5)}`;
  
  return fallbackId;
}

/**
 * Validates if a string matches the entity ID format
 */
export function isValidEntityId(id: string): boolean {
  const pattern = /^[a-z0-9]+_[a-z]+_[a-z]+_[a-z]+$/;
  return pattern.test(id);
}

/**
 * Extracts the entity type from an entity ID
 */
export function extractEntityTypeFromId(id: string): string | null {
  if (!isValidEntityId(id)) {
    return null;
  }
  return id.split('_')[0];
} 